import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Combobox } from "@/components/Combobox";
import { TaxOfficeSelect } from "@/components/TaxOfficeSelect";
import { ProvinceSelect, DistrictSelect, districtsOf } from "@/components/CitySelect";
import { taxIdInfo } from "@/lib/vkn";
import { contactOptions, isGenericContact } from "@/lib/contacts";
import { ShieldCheck, Loader2, UserPlus, Users, AlertTriangle, CheckCircle2, PencilLine } from "lucide-react";
import { toast } from "sonner";

export const blankRecipient = () => ({ mode: "existing", contact_id: "", edit: false, title: "", tax_no: "", tax_office: "", address: "", city: "", district: "", email: "", phone: "" });
const DETAIL_KEYS = ["tax_no", "tax_office", "address", "city", "district", "email", "phone"];

// Fatura alıcısı doğrulaması (GİB zorunlu alanlar: VKN/TCKN, ünvan, adres, il; VKN'de vergi dairesi)
export const recipientError = (r) => {
  if (r.mode === "existing") {
    if (!r.contact_id) return "Kayıtlı bir cari seçin";
    if (!r.edit) return "";
    if (r.tax_no && taxIdInfo(r.tax_no).valid !== true) return "Geçerli VKN (10 hane) veya TCKN (11 hane) girin";
    if (!r.address.trim() || !r.city.trim()) return "Adres ve il gerekli (GİB zorunlu alan)";
    return "";
  }
  if (!r.title.trim()) return "Alıcı ünvanı gerekli";
  if (isGenericContact(r.title)) return "Genel müşteri ünvanı (PERAKENDE vb.) fatura alıcısı olamaz";
  if (taxIdInfo(r.tax_no).valid !== true) return "Geçerli VKN (10 hane) veya TCKN (11 hane) girin";
  if (!r.address.trim() || !r.city.trim()) return "Adres ve il gerekli (GİB zorunlu alan)";
  if (r.tax_no.length === 10 && !r.tax_office.trim()) return "Vergi dairesi gerekli (VKN'li alıcı)";
  return "";
};

export const recipientPayload = (r) => (r.mode === "existing"
  ? { contact_id: r.contact_id, ...(r.edit ? Object.fromEntries(DETAIL_KEYS.map((k) => [k, String(r[k] || "").trim()])) : {}) }
  : { title: r.title.trim(), tax_no: r.tax_no, tax_office: r.tax_office, address: r.address.trim(), city: r.city, district: r.district, email: r.email.trim(), phone: r.phone.trim() });

const F = ({ label, className = "", children }) => (
  <div className={`space-y-1 min-w-0 ${className}`}><Label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>
);

// Vergi dairesi + adres + il/ilçe + iletişim (yeni cari ve kayıtlı cari düzenlemesi ortak)
function DetailFields({ value, set, tid }) {
  return (
    <>
      <F label={`Vergi Dairesi ${value.tax_no.length === 10 ? "*" : "(şahıs için gerekmez)"}`} className="sm:col-span-2"><TaxOfficeSelect value={value.tax_office} onChange={(v) => set({ tax_office: v })} data-testid={`${tid}-tax-office`} /></F>
      <F label="Adres *" className="sm:col-span-2"><Input value={value.address} onChange={(e) => set({ address: e.target.value })} placeholder="Mahalle, cadde, no" className="h-9 bg-white" data-testid={`${tid}-address`} /></F>
      <F label="İl *"><ProvinceSelect value={value.city} onChange={(v) => set({ city: v, district: districtsOf(v).includes(value.district) ? value.district : "" })} data-testid={`${tid}-city`} /></F>
      <F label="İlçe"><DistrictSelect city={value.city} value={value.district} onChange={(v) => set({ district: v })} data-testid={`${tid}-district`} /></F>
      <F label="E-posta (e-Arşiv için)"><Input value={value.email} onChange={(e) => set({ email: e.target.value })} placeholder="ornek@firma.com" className="h-9 bg-white" data-testid={`${tid}-email`} /></F>
      <F label="Telefon"><Input value={value.phone} onChange={(e) => set({ phone: e.target.value })} className="h-9 bg-white" data-testid={`${tid}-phone`} /></F>
    </>
  );
}

function ExistingContact({ value, set, contacts, tid, editable }) {
  const list = contacts.filter((c) => !isGenericContact(c.title));
  const opts = contactOptions(list, (c) => (c.vat_exempt ? { disabled: true, disabledReason: "KDV'siz cariye fatura kesilemez" } : {}));
  const sel = list.find((c) => c.id === value.contact_id);
  const info = taxIdInfo(value.tax_no);
  // Düzenle: carinin kayıtlı bilgileri forma dolar; fatura kesilirken değişenler cari karta da kaydedilir
  const startEdit = () => set({ edit: true, ...Object.fromEntries(DETAIL_KEYS.map((k) => [k, String(sel[k] || "")])), tax_no: String(sel.tax_no || "").replace(/\D/g, "") });
  return (
    <div className="space-y-2">
      <Combobox options={opts} value={sel?.title || ""} onChange={(t) => { const c = list.find((x) => x.title === t); set({ contact_id: c?.id || "", edit: false }); }} placeholder="Cari seçin (ünvan / VKN ile arayın)" searchPlaceholder="Ünvan / VKN / telefon ara..." data-testid={`${tid}-contact`} />
      {sel && !value.edit && (
        <div className="text-[11px] rounded-md border bg-white px-2.5 py-1.5 text-muted-foreground flex items-start gap-2" data-testid={`${tid}-contact-summary`}>
          <span className="min-w-0 flex-1"><b className="text-foreground">{sel.title}</b>{sel.tax_no ? ` · VKN/TCKN ${sel.tax_no}` : " · VKN/TCKN yok (kesim ekranında girin)"}{sel.address ? ` · ${[sel.address, sel.district, sel.city].filter(Boolean).join(" / ")}` : " · adres yok"}{sel.tax_office ? ` · ${sel.tax_office} VD` : ""}</span>
          {editable && <button type="button" onClick={startEdit} className="shrink-0 inline-flex items-center gap-1 font-bold text-indigo-700 hover:underline" data-testid={`${tid}-edit-btn`}><PencilLine className="h-3.5 w-3.5" />Düzenle</button>}
        </div>
      )}
      {sel && value.edit && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-2.5 space-y-2" data-testid={`${tid}-edit-panel`}>
          <div className="flex items-center justify-between gap-2 text-[11px]">
            <span className="font-bold text-indigo-900">{sel.title} · bilgileri düzenle</span>
            <button type="button" onClick={() => set({ edit: false })} className="font-semibold text-muted-foreground hover:underline" data-testid={`${tid}-edit-cancel`}>Vazgeç</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <F label="VKN (10) / TCKN (11)" className="sm:col-span-2">
              <Input value={value.tax_no} onChange={(e) => set({ tax_no: e.target.value.replace(/\D/g, "") })} maxLength={11} className={`h-9 bg-white tabular-nums ${info.valid === false ? "border-red-400" : ""}`} data-testid={`${tid}-edit-vkn`} />
              {info.valid === false && <p className="text-[10px] text-red-600">{info.message}</p>}
            </F>
            <DetailFields value={value} set={set} tid={`${tid}-edit`} />
          </div>
          <p className="text-[10.5px] text-indigo-900/80">Değişiklikler fatura kesilirken <b>{sel.title}</b> cari kartına da kaydedilir.</p>
        </div>
      )}
    </div>
  );
}

function NewContact({ value, set, contacts, tid }) {
  const [chk, setChk] = useState(null);
  const [busy, setBusy] = useState(false);
  const info = taxIdInfo(value.tax_no);
  const owner = value.tax_no.length >= 10 ? contacts.find((c) => c.tax_no === value.tax_no && !c.parent_id && !isGenericContact(c.title)) : null;
  const lookup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/qnb/check-taxpayer", { vkn: value.tax_no });
      setChk(data);
      if ((data.unvan || "").trim()) set({ title: data.unvan.trim() });
      toast.success(data.registered ? `e-Fatura mükellefi · ${data.unvan || ""}` : "e-Fatura mükellefi değil — e-Arşiv kesilir");
    } catch (e) { setChk(null); toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      <F label="VKN (10) / TCKN (11) *" className="sm:col-span-2">
        <div className="flex gap-1.5">
          <Input value={value.tax_no} onChange={(e) => { set({ tax_no: e.target.value.replace(/\D/g, "") }); setChk(null); }} maxLength={11} placeholder="Vergi / TC Kimlik No" className={`h-9 bg-white tabular-nums ${info.valid === false ? "border-red-400" : ""}`} data-testid={`${tid}-vkn`} />
          <Button type="button" size="sm" variant="secondary" className="h-9 shrink-0" onClick={lookup} disabled={busy || info.valid !== true} data-testid={`${tid}-lookup-btn`}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}<span className="ml-1">Mükellef Sorgula</span></Button>
        </div>
        {info.valid === false && <p className="text-[10px] text-red-600" data-testid={`${tid}-vkn-warn`}>{info.message}</p>}
        {chk && <p className={`text-[10px] font-semibold flex items-center gap-1 ${chk.registered ? "text-indigo-700" : "text-sky-700"}`} data-testid={`${tid}-lookup-result`}><CheckCircle2 className="h-3 w-3" />{chk.registered ? `e-Fatura mükellefi · GİB ünvanı: ${chk.unvan || "—"}` : "e-Fatura mükellefi değil → e-Arşiv"}</p>}
      </F>
      <F label="Alıcı Ünvanı (resmi) *" className="sm:col-span-2"><Input value={value.title} onChange={(e) => set({ title: e.target.value })} placeholder="Firma / kişi resmi ünvanı" className="h-9 bg-white" data-testid={`${tid}-title`} /></F>
      {owner && (
        <div className="sm:col-span-2 text-[11px] rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-amber-900 flex items-center gap-2 flex-wrap" data-testid={`${tid}-vkn-owner`}>
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />Bu VKN/TCKN <b>{owner.title}</b> carisinde kayıtlı.
          <button type="button" className="font-bold underline" onClick={() => set({ mode: "existing", contact_id: owner.id })} data-testid={`${tid}-use-owner`}>Bu cariyi seç</button>
        </div>
      )}
      <DetailFields value={value} set={set} tid={tid} />
    </div>
  );
}

// Fatura alıcısı seçimi: kayıtlı cari ya da yeni cari (vergi bilgileri + QNB mükellef sorgusu). Genel müşteri (PERAKENDE) seçilemez.
export default function InvoiceRecipientPicker({ value, onChange, contacts = [], editable = false, ...props }) {
  const tid = props["data-testid"] || "recipient";
  const set = (patch) => onChange({ ...value, ...(patch.mode && patch.mode !== value.mode ? { edit: false } : {}), ...patch });
  const tab = (mode, Icon, label) => (
    <Button type="button" size="sm" variant={value.mode === mode ? "default" : "outline"} className="flex-1" onClick={() => set({ mode })} data-testid={`${tid}-mode-${mode}`}><Icon className="h-3.5 w-3.5 mr-1.5" />{label}</Button>
  );
  return (
    <div className="space-y-2.5" data-testid={tid}>
      <div className="flex gap-2">{tab("existing", Users, "Kayıtlı Cari")}{tab("new", UserPlus, "Yeni Cari Aç")}</div>
      {value.mode === "existing"
        ? <ExistingContact value={value} set={set} contacts={contacts} tid={tid} editable={editable} />
        : <NewContact value={value} set={set} contacts={contacts} tid={tid} />}
    </div>
  );
}
